import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, MessageCircle, Mail, ArrowUpRight } from "lucide-react";
import { useSite } from "@/lib/site";
import { useSeo } from "@/hooks/useSeo";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);
}

export default function ThankYou() {
  const site = useSite();
  useSeo({ title: "Thank you | Cirencester 4x4", path: "/thank-you" });
  useNoIndex();
  const wa = (site.whatsapp || "").replace(/\D/g, "");
  return (
    <section className="mx-auto max-w-3xl px-5 pb-28 pt-36 sm:px-8 lg:pt-44" data-testid="thank-you-page">
      <p className="eyebrow text-forest">Thank you</p>
      <h1 className="mt-5 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">Enjoy every mile.</h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/80">
        Thank you for buying from Cirencester 4x4. We hope your new vehicle serves you well for years to come. If you have a moment, a short review helps other buyers find a dealer they can trust, and it means a great deal to a small business like ours.
      </p>
      <div className="mt-10 border border-stone bg-paper p-6 sm:p-8">
        <div className="flex gap-1 text-forest" aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5" strokeWidth={1.5} />)}</div>
        <p className="mt-4 font-serif text-3xl text-charcoal">How did we do?</p>
        {site.google_review_url ? (
          <a href={site.google_review_url} target="_blank" rel="noreferrer" className="btn btn-primary mt-6 w-full justify-center !py-5 text-base sm:w-auto" data-testid="google-review-button">
            Leave us a Google review <ArrowUpRight className="arrow h-4 w-4" />
          </a>
        ) : (
          <p className="mt-4 text-sm leading-relaxed text-charcoal/80" data-testid="google-review-pending">Our Google review page is coming soon. In the meantime we would love to hear how you are getting on.</p>
        )}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="link-u inline-flex items-center gap-2" data-testid="thank-you-whatsapp"><MessageCircle className="h-4 w-4" /> Send feedback on WhatsApp</a>}
          {site.email && <a href={`mailto:${site.email}?subject=Feedback`} className="link-u inline-flex items-center gap-2" data-testid="thank-you-email"><Mail className="h-4 w-4" /> Email us</a>}
        </div>
      </div>
      <Link to="/stock" className="link-u mt-10 inline-block text-sm text-forest" data-testid="thank-you-stock-link">See what else is in stock</Link>
    </section>
  );
}
